import { useMemo, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import {
  segmentPositionLabel,
  buildCombinedTranscript,
  activeSegmentCount,
  type VisitVoiceRecordingRow,
} from '@helios/data-access';

/* ------------------------------------------------------------------ */
/*  TranscriptDrawer (PRD.273)                                        */
/*                                                                    */
/*  Right-side drawer (shared @helios/ui/overlay Drawer → focus trap, */
/*  Esc, body-scroll-lock, focus return) showing the immutable RAW    */
/*  transcript for one recording, plus copy, multi-segment context,   */
/*  a combined view, and shared extraction + allergy-review actions.   */
/*  Never edits the transcript or the Chief Complaint.                 */
/* ------------------------------------------------------------------ */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recording: VisitVoiceRecordingRow | null;
  /** All Chief Complaint recordings (for segment position + combined view). */
  recordings: VisitVoiceRecordingRow[];
  brandTokens: Record<string, string>;
  // Shared extraction state/actions (same handler as the card).
  extractionLabel: string;
  extractionDisabled: boolean;
  extractionRunning: boolean;
  extractionError?: string | null;
  extractionStatusText: string;
  canExtract: boolean;
  onExtract: () => void;
  // Allergy review (PRD.272).
  allergyReviewCount: number;
  canReviewAllergies: boolean;
  onReviewAllergies: () => void;
}

export function TranscriptDrawer({
  open,
  onOpenChange,
  recording,
  recordings,
  brandTokens,
  extractionLabel,
  extractionDisabled,
  extractionRunning,
  extractionError,
  extractionStatusText,
  canExtract,
  onExtract,
  allergyReviewCount,
  canReviewAllergies,
  onReviewAllergies,
}: Props) {
  const [showCombined, setShowCombined] = useState(false);
  const [copied, setCopied] = useState(false);

  const sorted = useMemo(
    () => [...recordings].sort((a, b) => (a.recorded_at ?? '').localeCompare(b.recorded_at ?? '')),
    [recordings],
  );
  const activeCount = useMemo(
    () =>
      activeSegmentCount(
        sorted.map((r) => ({ recorded_at: r.recorded_at, transcript: r.transcript })),
      ),
    [sorted],
  );
  const index = recording ? sorted.findIndex((r) => r.id === recording.id) : -1;
  const segmentLabel = segmentPositionLabel(index, sorted.length);
  const multiSegment = sorted.length > 1;

  const combined = useMemo(
    () =>
      buildCombinedTranscript(
        sorted.map((r) => ({ recorded_at: r.recorded_at, transcript: r.transcript })),
        (recordedAt, i) => `Recording ${i + 1} — ${formatTimestamp(recordedAt)}`,
      ),
    [sorted],
  );

  const transcript = recording?.transcript ?? '';
  const displayText = showCombined ? combined : transcript;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — no-op, never throws into the UI */
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Recording transcript"
      width="min(560px, 96vw)"
      portalStyle={brandTokens as CSSProperties}
    >
      <div
        style={{
          padding: 'var(--sp-5, 20px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4, 16px)',
        }}
      >
        {/* Metadata */}
        {recording && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
            }}
          >
            <div>Recorded {formatTimestamp(recording.recorded_at)}</div>
            <div>
              Duration {formatDuration(recording.duration_ms ? recording.duration_ms / 1000 : 0)}
            </div>
            <div>Transcription: {recording.transcription_status ?? 'unknown'}</div>
            {(recording.upload_status === 'pending' ||
              recording.upload_status === 'uploading' ||
              recording.upload_status === 'failed') && <div>Upload: {recording.upload_status}</div>}
            {segmentLabel && <div>{segmentLabel}</div>}
          </div>
        )}

        {/* Multi-segment note */}
        {multiSegment && (
          <div style={noteStyle}>
            This recording is one of {sorted.length} segments. Clinical extraction uses all active
            Chief Complaint recordings.
          </div>
        )}

        {/* Combined toggle */}
        {multiSegment && activeCount > 1 && (
          <button
            type="button"
            onClick={() => setShowCombined((v) => !v)}
            style={linkBtn}
            aria-pressed={showCombined}
          >
            {showCombined ? 'View this segment only' : 'View combined transcript'}
          </button>
        )}

        {/* Raw transcript — read-only, preserves line breaks, scrollable */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={sectionLabel}>
              {showCombined ? 'COMBINED TRANSCRIPT' : 'RAW TRANSCRIPT'}
            </span>
            <button
              type="button"
              onClick={() => void copy()}
              disabled={!displayText.trim()}
              aria-label="Copy transcript"
              style={{ ...linkBtn, opacity: displayText.trim() ? 1 : 0.5 }}
            >
              Copy transcript
            </button>
          </div>
          <div
            tabIndex={0}
            aria-readonly="true"
            aria-label="Raw transcript"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 320,
              overflow: 'auto',
              padding: 'var(--sp-3, 12px)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-muted, var(--bg-surface))',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text)',
              userSelect: 'text',
            }}
          >
            {displayText.trim() ? displayText : 'Transcript is not available yet.'}
          </div>
          <div
            aria-live="polite"
            style={{
              fontSize: 'var(--font-size-2xs, 10px)',
              color: 'var(--text-muted)',
              minHeight: 14,
            }}
          >
            {copied ? 'Transcript copied' : ''}
          </div>
        </div>

        {/* Clinical extraction (shared handler/state with the card) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            borderTop: '1px solid var(--border)',
            paddingTop: 'var(--sp-3, 12px)',
          }}
        >
          <span style={sectionLabel}>CLINICAL EXTRACTION</span>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: extractionError ? '#dc2626' : 'var(--text-muted)',
            }}
          >
            {extractionError ?? extractionStatusText}
          </span>
          {canExtract && (
            <button
              type="button"
              onClick={onExtract}
              disabled={extractionDisabled}
              aria-label={extractionLabel}
              style={{
                ...primaryBtn,
                opacity: extractionDisabled ? 0.5 : 1,
                alignSelf: 'flex-start',
              }}
            >
              {extractionRunning ? 'Extracting…' : extractionLabel}
            </button>
          )}
        </div>

        {/* Allergy attention — opens the existing PRD.272 modal (not duplicated here) */}
        {canReviewAllergies && allergyReviewCount > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              borderTop: '1px solid var(--border)',
              paddingTop: 'var(--sp-3, 12px)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-xs)' }}>
              {allergyReviewCount} {allergyReviewCount === 1 ? 'allergy needs' : 'allergies need'}{' '}
              review
            </span>
            <button
              type="button"
              onClick={onReviewAllergies}
              style={primaryBtn}
              aria-label="Review allergies"
            >
              Review allergies
            </button>
          </div>
        )}
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const sectionLabel: CSSProperties = {
  fontSize: 'var(--font-size-2xs, 10px)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
};

const noteStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  padding: '6px 8px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-muted, var(--bg-surface))',
  border: '1px solid var(--border)',
};

const linkBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-primary)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
  textDecoration: 'underline',
  alignSelf: 'flex-start',
};

const primaryBtn: CSSProperties = {
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};
